import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { HotkeyManager } from "capitalrx-hotkeys/lib";

import { DISPLAY_NAMES } from "../constants";
import type { HotkeyContextValue, HotkeyProviderProps } from "../types";

export const HotkeyContext = createContext<HotkeyContextValue | null>(null);

export function HotkeyProvider({ children, manager: externalManager }: HotkeyProviderProps) {
  const [enabled, setEnabled] = useState(true);
  const [manager] = useState(() => externalManager || new HotkeyManager());

  useEffect(() => {
    // Start listening for keyboard events
    manager.start?.();

    return () => {
      // Stop listening and cleanup
      manager.stop?.();
      if (!externalManager) {
        manager.destroy?.();
      }
    };
  }, [manager, externalManager]);

  const contextValue = useMemo<HotkeyContextValue>(
    () => ({
      manager,
      enabled,
      setEnabled,
    }),
    [manager, enabled],
  );

  return <HotkeyContext.Provider value={contextValue}>{children}</HotkeyContext.Provider>;
}

HotkeyProvider.displayName = DISPLAY_NAMES.HotkeyProvider;

export function useHotkeyContext(): HotkeyContextValue {
  const context = useContext(HotkeyContext);
  if (!context) {
    throw new Error("useHotkeyContext must be used within a HotkeyProvider or HotKeyRoot");
  }
  return context;
}
